import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { BasicContainer } from '@minecraft.atoms';
import { addToDate, getStartOfDay, parseTextToISODateStr } from '@minecraft.date-utils';
import Icon from '@minecraft.icon';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';

import 'react-datepicker/dist/react-datepicker.css';

/** this styles the datepicker wrapper & other associated icons / pieces */
const StyledDatePickerWrapper = styled.div`
  width: 100%;
  position: relative;

  & .react-datepicker-wrapper {
    width: 100%;
  }

  & .react-datepicker__close-icon {
    right: 0.75rem;
  }

  .react-datepicker__close-icon::after {
    background-color: ${(props) => props.theme.designSystem.bgColors.card};
    color: ${(props) => props.theme.designSystem.textColors.default};
    font-size: 1.5rem;
  }

  // sets the background color of the currently selected day in the calendar
  .react-datepicker__day--selected {
    background-color: ${(props) => props.theme.designSystem.bgColors.primary};
  }

  // sets the background color of the currently focused day
  .react-datepicker__day--keyboard-selected {
    background-color: ${(props) => props.theme.designSystem.bgColors.lightContrast};
  }

  // sets the vertical alignment of the prev / before navigation icons
  .react-datepicker__navigation-icon {
    top: 0.25rem;
  }

  // sets the vertical alignment of the year / month dropdown arrows
  .react-datepicker__year-read-view--down-arrow {
    top: 20%;
  }

  // sets the vertical alignment of the year / month dropdown arrows
  .react-datepicker__month-read-view--down-arrow {
    top: 20%;
  }

  // adds chevron indicator arrow to the showYearsDropdown at the top / bottom of the list
  .react-datepicker__navigation--years::before {
    border-color: ${(props) => props.theme.designSystem.borderColors.emphasis};
    border-style: solid;
    border-width: 0.1875rem 0.1875rem 0 0;
    content: '';
    display: block;
    height: 0.5625rem;
    left: 0.6875rem;
    position: absolute;
    width: 0.5625rem;
  }

  // styles the indicator chevron in the top showYearsDropdown specifically
  .react-datepicker__navigation--years-upcoming::before {
    top: 1.0625rem;
    transform: rotate(315deg);
  }

  // styles the indicator chevron in the bottom showYearsDropdown specifically
  .react-datepicker__navigation--years-previous::before {
    top: 0.375rem;
    transform: rotate(135deg);
  }

  // hides the normal month/year display when the showYear & showMonth dropdowns are shown
  .react-datepicker__current-month.react-datepicker__current-month--hasYearDropdown.react-datepicker__current-month--hasMonthDropdown {
    display: none;
  }

  // styles the default color for the naviation icons
  .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__month-year-read-view--down-arrow,
  .react-datepicker__navigation-icon:before {
    border-color: ${(props) => props.theme.designSystem.borderColors.heavyOutline};
  }

  // styles the hover states of the navigation icons
  .react-datepicker__year-read-view:hover .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__year-read-view:hover .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__month-read-view:hover .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-read-view:hover .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__month-year-read-view:hover .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-year-read-view:hover .react-datepicker__month-read-view--down-arrow {
    border-color: ${(props) => props.theme.designSystem.borderColors.heavyOutline};
  }
`;

/** this styles the datepicker input itself, these styles should match essentially how the BaseInput styles are done */
const StyledDatePickerInput = styled(DatePicker)<{ $error: boolean }>`
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ theme }) => theme.designSystem.textColors.default};
  background: ${({ theme }) => theme.designSystem.bgColors.input};
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.input)};
  border-radius: 0.25rem;
  outline: none;

  &&:focus,
  &&:focus-visible {
    box-shadow: none;
    border: 1px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.inputFocus};
    outline: 4px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.inputFocusError : theme.designSystem.borderColors.inputFocusOutline};
  }

  &&:disabled {
    box-shadow: none;
    background: ${({ theme }) => theme.designSystem.bgColors.inputDisabled};
    border: 1px solid ${({ theme }) => theme.designSystem.borderColors.input};
  }
`;

const DATE_DISPLAY_FORMAT = 'MM/dd/yy';

const normalizeInputDateStr = (value: string): Date => {
  // make sure that the string parses to an actual valid date
  // we also remove the "Z" off of any UTC date string so that the
  // Date() constructor will not offset the date to the local timezone
  // otherwise it can cause people with negative UTC offsets to see the date
  // on the "previous" day or people with positive UTC offsets to see the date
  // as the "next" day
  const parsedStr = parseTextToISODateStr({ inputText: value });

  if (!parsedStr) {
    return null;
  }

  const startOfDay = getStartOfDay(parsedStr);
  return new Date(addToDate({ date: startOfDay, amount: 12, unit: 'hours' }));
};

export type BaseDatePickerProps = {
  /**
   * callback invoked with the value of the text field
   */
  onChange?: (newValue: string) => void;
  /** callback invoked when the input loses focus */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * current selected date of this input as an ISO date string
   */
  value: string;
  /** minDate the "earliest" selectable value, anything before this will be disabled
   * This should be an ISO date string
   */
  minDate?: string;
  /** maxDate the "latest" selectable value, anything after this will be disabled
   * This should be an ISO date string
   */
  maxDate?: string;
  /** test id value */
  'data-testid'?: string;
  /** qa id value */
  'data-qa-id'?: string;
  /** name attribute for the input */
  name: string;
  /** disabled state for the input */
  disabled?: boolean;
  /** whether there is a validation error on this form field */
  invalid?: boolean;
};

/**
 * A simple, controlled datepicker input
 * @name BaseDatePickerInput
 * @description this is to be used as a base for other datepicker input components, do not import this directly into application code
 * it does not include a label, help text, validation or other form field features
 *
 * @example
 * const [value, setSelectedValue] = useState<string>('');
 * <BaseDatePicker value={value} onChange={setSelectedValue} />
 */
export const BaseDatePicker: FC<BaseDatePickerProps> = ({
  onBlur,
  onChange,
  'data-testid': testId,
  'data-qa-id': qaId,
  value,
  name,
  disabled,
  minDate: minDateProp,
  maxDate: maxDateProp,
  invalid,
}) => {
  const dateFormat = DATE_DISPLAY_FORMAT;
  // we memoize the selectedDate value on its own b/c it's likely to change much more often
  // than the min/max date values
  const selectedDate = useMemo((): Date => {
    if (typeof value === 'string') {
      return normalizeInputDateStr(value);
    }

    return null;
  }, [value]);
  const { minDate, maxDate } = useMemo(() => {
    return {
      minDate: typeof minDateProp === 'string' ? normalizeInputDateStr(minDateProp) : undefined,
      maxDate: typeof maxDateProp === 'string' ? normalizeInputDateStr(maxDateProp) : undefined,
    };
  }, [minDateProp, maxDateProp]);

  // callback that takes the output from the datepicker as a JS Date() object
  // and maps it back into an ISO string (or empty string if empty or invalid)
  const handleChange = useCallback(
    (selectedDate: Date | null) => {
      let newVal = '';
      // this is called when the user selects a new date in the calendar picker
      // it should always produce a valid JS Date() object
      if (selectedDate instanceof Date) {
        newVal = selectedDate.toISOString();
      } else {
        newVal = parseTextToISODateStr({
          inputText: selectedDate,
          format: dateFormat,
        });
      }
      onChange(newVal);
    },
    [onChange, dateFormat]
  );

  return (
    <StyledDatePickerWrapper data-testid="datepicker-wrapper">
      <StyledDatePickerInput
        selected={selectedDate}
        dateFormat={dateFormat}
        minDate={minDate}
        maxDate={maxDate}
        onChange={handleChange}
        isClearable={false}
        onBlur={(e) => {
          onBlur?.(e);
        }}
        id={name}
        name={name}
        $error={invalid}
        disabled={disabled}
        data-testid={testId}
        data-qa-id={qaId}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        placeholderText={dateFormat?.toUpperCase()}
        wrapperClassName="base-datepicker"
        autoComplete="off"
      />
      <BasicContainer className="cn_absolute cn_pointer-events-none" top={0.25} right={0.5}>
        <Icon name="calendar" size="mmd" />
      </BasicContainer>
    </StyledDatePickerWrapper>
  );
};

export interface WrappedBaseDatePickerProps {
  wrapperProps?: FieldWrapperProps;
  inputProps?: BaseDatePickerProps;
}

/**
 * A controlled datepicker input with the standard form field wrapper
 * @name WrappedBaseDatePicker
 * @description this is to be used as a base for other datepicker components, do not import this directly into application code
 * it should be setup with a formik <Field /> around it which maps value, onChange, name, validation, etc into the props
 *
 * @example
 * const [value, setSelectedValue] = useState<string>('');
 * <WrappedBaseDatePickerInput
 *    inputProps={{ value, onChange: setSelectedValue, type: 'text', name: 'input' }}
 *    wrapperProps={{ label: 'My Input', helpText: 'Please Fill This Out', id: 'input' }}
 * />
 */
export const WrappedBaseDatePicker: FC<WrappedBaseDatePickerProps> = ({ wrapperProps = {}, inputProps }) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <BaseDatePicker {...inputProps} />
    </FieldWrapper>
  );
};
